import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import Core from '../bells/practice-core';
import type { Piece, Plan, PlanConfig, Transport, PieceNote } from '../bells/practice-core';
import { BellAudio } from '../bells/bellAudio';
import { ScoreView } from '../bells/scoreView';
import './BellsPage.css';

// The bells practice page, sl2music.com/bells (app/bells/README.md). A ringer chooses
// their pair of bells, sees their part with the melody above it and plays along with the
// rest of the choir, the whole piece or sections they mark out themselves. Without
// bells at home they ring along on the keyboard (F for the left hand's bell, J for the
// right's) or with their thumbs on a phone, and see how each stroke lined up.
// Signed in with the choir's shared password (the sl2choir account); nothing is saved
// on the server: sections and settings are kept in the browser.

const PIECE = 'bring-a-torch';
const CHOIR_ACCOUNT = 'sl2choir';
const NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
const bellName = (writtenMidi: number) => NAMES[(writtenMidi + 12) % 12] + (Math.floor((writtenMidi + 12) / 12) - 1);

interface PairInfo { id: string; label: string; ready: boolean; views?: { wide: string; phone: string } }
interface BellsIndex {
  slug: string; title: string; arranger: string; occasion: string; marked: [number, number];
  defaultTempo: number; full?: string; pairs: PairInfo[];
}
interface Section { id: string; name: string; from: number; to: number }
type Choice = { kind: 'whole' } | { kind: 'section'; id: string } | { kind: 'from'; measure: number };
type Hand = 'L' | 'R';
interface Press { pass: number; local: number; hand: Hand }
interface Mark { local: number; hand: Hand; status: 'on' | 'near' | 'off' | 'missed'; delta: number; midi: number }
interface Result { from: number; to: number; beats: number; marks: Mark[]; extras: { local: number; hand: Hand }[]; spans: Plan['spans']; bpb: number }

const store = {
  get<T>(key: string, fallback: T): T { try { const v = localStorage.getItem(`bells:${key}`); return v == null ? fallback : JSON.parse(v); } catch { return fallback; } },
  set(key: string, value: unknown) { try { localStorage.setItem(`bells:${key}`, JSON.stringify(value)); } catch { /* private window */ } },
};
const isPhone = () => window.innerWidth < 768;

function BellsSignIn() {
  const { login } = useAuth();
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await login(CHOIR_ACCOUNT, pw); } catch { setErr('That password did not work. It is the same one the choir uses on this site.'); }
    setBusy(false);
  };
  return (
    <div className="bells bl-signin-wrap">
      <form className="bl-signin" onSubmit={submit}>
        <p className="bl-eb">St. Luke's ~ San Lucas · Bells</p>
        <h2>Bring a Torch</h2>
        <p className="bl-muted">Sign in with the choir password to practise your bells.</p>
        <label className="bl-lab" htmlFor="bl-pw">Choir password</label>
        <input id="bl-pw" type="password" autoComplete="current-password" value={pw} onChange={e => setPw(e.target.value)} autoFocus />
        {err && <p className="bl-err">{err}</p>}
        <button className="bl-btn" type="submit" disabled={busy || !pw}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <small>This browser stays signed in, so you only do this once.</small>
      </form>
    </div>
  );
}

const BellsPage: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  // The site's body is padded for its fixed navigation bar, which this page does not show.
  useEffect(() => { document.body.classList.add('bells-body'); return () => document.body.classList.remove('bells-body'); }, []);
  if (isLoading) return <div className="bells bl-loading">Loading…</div>;
  if (!isAuthenticated) return <BellsSignIn />;
  return <BellsPlayer signedInAs={user?.username === CHOIR_ACCOUNT ? 'the choir' : (user?.username || '')} />;
};

function BellsPlayer({ signedInAs }: { signedInAs: string }) {
  const [index, setIndex] = useState<BellsIndex | null>(null);
  const [pieces, setPieces] = useState<Record<string, Piece>>({});
  const [svgText, setSvgText] = useState('');
  const [error, setError] = useState('');
  const [pairId, setPairId] = useState<string>(() => store.get('pair', ''));
  const [picking, setPicking] = useState(false);
  const [phone, setPhone] = useState(isPhone());
  const [size, setSize] = useState<{ mode: 'whole' } | { mode: 'zoom'; z: number }>(() => store.get('size', { mode: 'zoom', z: 1 }));
  const [sections, setSections] = useState<Section[]>(() => store.get(`${PIECE}:sections`, []));
  const [choice, setChoice] = useState<Choice>({ kind: 'whole' });
  const [tempo, setTempo] = useState<number>(() => store.get('tempo', 0));
  const [countBars, setCountBars] = useState<number>(() => store.get('countBars', 1));
  // What plays: the ringer's own two bells alone, or the whole choir (Jack, 2026-11-03).
  // The melody above the part is there to read, never sounded.
  const [plays, setPlays] = useState<'mine' | 'full'>(() => store.get('plays', 'full'));
  const [fullPiece, setFullPiece] = useState<Piece | null>(null);
  const [clicks, setClicks] = useState<boolean>(() => store.get('clicks', false));
  const [loop, setLoop] = useState<boolean>(() => store.get('loop', true));
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState('Stopped');
  const [measureNow, setMeasureNow] = useState<number | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [form, setForm] = useState({ from: '', to: '', name: '' });
  const [formErr, setFormErr] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'sections' | 'more' | null>(null);
  const [pressed, setPressed] = useState<Hand | null>(null);
  // The size of the music: 'whole' fits the whole piece on the screen, in columns where
  // there is room; otherwise a zoom, 1 being the width of the panel.
  const [whole, setWhole] = useState<{ cols: { y0: number; y1: number }[]; scale: number; x: number; w: number } | null>(null);
  const [win, setWin] = useState({ w: window.innerWidth, h: window.innerHeight });

  const audioRef = useRef<BellAudio | null>(null);
  const transportRef = useRef<Transport | null>(null);
  const planRef = useRef<Plan | null>(null);
  const configRef = useRef<PlanConfig | null>(null);
  const viewRef = useRef<ScoreView | null>(null);
  const scoreBox = useRef<HTMLDivElement | null>(null);
  const svgHost = useRef<HTMLDivElement | null>(null);
  const pressesRef = useRef<Press[]>([]);
  const lastPassRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const systemRef = useRef<number>(-1);
  const dragRef = useRef<{ anchor: number; moved: boolean } | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  // ---------- loading
  useEffect(() => {
    apiClient.get(`/bells/${PIECE}`).then(r => {
      const ix = r.data as BellsIndex; setIndex(ix);
      if (!store.get('tempo', 0)) setTempo(ix.defaultTempo);
      Promise.all(ix.pairs.filter(p => p.ready && p.views).map(p =>
        apiClient.get(`/bells/${PIECE}/${p.views!.wide}/piece`).then(x => [p.id, x.data as Piece] as const)))
        .then(list => setPieces(Object.fromEntries(list)))
        .then(() => (ix.full ? apiClient.get(`/bells/${PIECE}/${ix.full}/piece`).then(x => setFullPiece(x.data as Piece)) : undefined))
        .catch(() => setError('The music could not be loaded. Try reloading the page.'));
    }).catch(() => setError('The music could not be loaded. Try reloading the page.'));
  }, []);
  useEffect(() => {
    let t = 0;
    const f = () => { setPhone(isPhone()); clearTimeout(t); t = window.setTimeout(() => setWin({ w: window.innerWidth, h: window.innerHeight }), 120); };
    window.addEventListener('resize', f); return () => { window.removeEventListener('resize', f); clearTimeout(t); };
  }, []);
  useEffect(() => store.set('size', size), [size]);
  const pair = index?.pairs.find(p => p.id === pairId && p.ready) || null;
  useEffect(() => { if (index && !pair) setPicking(true); }, [index, pair]);
  useEffect(() => {
    if (!pair?.views) { setSvgText(''); return; }
    // A phone reads four bars to a line, except in the whole-piece view, where the wider
    // lines of eight make the music larger.
    const narrow = phone && size.mode !== 'whole';
    apiClient.get(`/bells/${PIECE}/${narrow ? pair.views.phone : pair.views.wide}/score`, { responseType: 'text', transformResponse: x => x })
      .then(r => setSvgText(r.data as string)).catch(() => setError('The music could not be loaded. Try reloading the page.'));
  }, [pair, phone, size.mode]);

  useEffect(() => store.set('tempo', tempo), [tempo]);
  useEffect(() => store.set('countBars', countBars), [countBars]);
  useEffect(() => store.set('plays', plays), [plays]);
  useEffect(() => store.set('clicks', clicks), [clicks]);
  useEffect(() => store.set('loop', loop), [loop]);
  useEffect(() => store.set(`${PIECE}:sections`, sections), [sections]);

  // The piece as it sounds: the measures of the ringer's view, the melody cue and every
  // pair whose part is entered.
  const piece: Piece | null = useMemo(() => {
    const base = pair ? pieces[pair.id] : null;
    if (!base) return null;
    // The ringer's own part, and the whole arrangement (the master's four bell streams)
    // or, until that has loaded, every pair entered. The melody cue is left out: it is
    // there to read.
    const parts = base.parts.filter(p => p.id === pair!.id);
    const others = fullPiece ? fullPiece.parts.map(p => ({ ...p, id: `full-${p.id}` }))
      : Object.values(pieces).flatMap(p => p.parts).filter(p => p.id !== 'cue' && p.id !== pair!.id);
    for (const part of others) if (!parts.some(x => x.id === part.id)) parts.push(part);
    return { ...base, parts };
  }, [pair, pieces, fullPiece]);
  const myNotes: PieceNote[] = useMemo(() => (piece && pair ? piece.parts.find(p => p.id === pair.id)?.notes || [] : []), [piece, pair]);
  const lowMidi = useMemo(() => (myNotes.length ? Math.min(...myNotes.map(n => n.midi)) : 72), [myNotes]);
  const highMidi = useMemo(() => (myNotes.length ? Math.max(...myNotes.map(n => n.midi)) : 74), [myNotes]);
  const handOf = useCallback((midi: number): Hand => (midi === lowMidi ? 'L' : 'R'), [lowMidi]);
  // A one-bell part (C4, C7): either key rings the one bell, and there is one lane and one key box.
  const solo = !!pair && !pair.label.includes(' · ');
  const N = piece?.measureCount || 0;

  // ---------- the score
  useEffect(() => {
    const host = svgHost.current;
    viewRef.current = null;
    if (!host || !svgText || !piece) return;
    host.innerHTML = svgText;
    const svg = host.querySelector('svg');
    if (!svg) return;
    svg.removeAttribute('width'); svg.removeAttribute('height');
    try { viewRef.current = new ScoreView(svg as SVGSVGElement, piece); } catch { setError('The music could not be drawn.'); }
    systemRef.current = -1;
  }, [svgText, piece]);

  // The zoom: the music's width as a share of the panel's (more than the panel scrolls).
  useEffect(() => {
    const svg = svgHost.current?.querySelector('svg');
    if (svg) svg.style.width = size.mode === 'zoom' ? `${size.z * 100}%` : '100%';
  }, [size, svgText]);
  // The whole piece: the lines of music laid out in as many columns as make them largest
  // while all of them fit the panel.
  useEffect(() => {
    const v = viewRef.current, box = scoreBox.current;
    if (size.mode !== 'whole' || !v || !box) { setWhole(null); return; }
    const sys = v.systems(), vbW = v.width(), heights = sys.map(s => s.y1 - s.y0), total = heights.reduce((a, b) => a + b, 0);
    const W = box.clientWidth - 24, gap = 18;
    const H = win.w >= 2200 ? box.clientHeight - 12 : Math.max(280, win.h - (win.w < 768 ? 300 : 170));
    let best = { scale: 0, cols: [] as { y0: number; y1: number }[] };
    for (let c = 1; c <= (win.w < 768 ? 1 : 6); c++) {
      const target = total / c, cols: number[][] = [[]];
      let acc = 0;
      heights.forEach((h, i) => {
        if (cols[cols.length - 1].length && acc + h / 2 > target && cols.length < c) { cols.push([]); acc = 0; }
        cols[cols.length - 1].push(i); acc += h;
      });
      const colH = Math.max(...cols.map(cl => cl.reduce((a, i) => a + heights[i], 0)));
      const scale = Math.min((W - gap * (cols.length - 1)) / cols.length / vbW, H / colH);
      if (scale > best.scale * 1.02) best = { scale, cols: cols.map(cl => ({ y0: sys[cl[0]].y0, y1: sys[cl[cl.length - 1]].y1 })) };
    }
    setWhole({ ...best, x: v.left(), w: vbW });
  }, [size, svgText, piece, win]);
  const zoomBy = (d: number) => setSize(cur => {
    const z = cur.mode === 'zoom' ? cur.z : 1;
    return { mode: 'zoom', z: Math.round(Math.max(0.3, Math.min(2, z + d)) * 10) / 10 };
  });

  const sectionRange = useCallback((c: Choice): [number, number] | null => {
    if (!piece) return null;
    if (c.kind === 'section') { const s = sections.find(x => x.id === c.id); return s ? [s.from, s.to] : null; }
    if (c.kind === 'from') return [c.measure, piece.measureCount];
    return [1, piece.measureCount];
  }, [piece, sections]);
  useEffect(() => {
    const v = viewRef.current; if (!v) return;
    const r = choice.kind === 'section' ? sectionRange(choice) : null;
    v.showSection(r ? r[0] : null, r ? r[1] : null);
  }, [choice, sectionRange, svgText]);

  // ---------- sound and transport
  const ensureAudio = () => {
    if (!audioRef.current) audioRef.current = new BellAudio();
    audioRef.current.begin();
    return audioRef.current;
  };
  const applyLevels = useCallback(() => {
    const a = audioRef.current; if (!a || !piece || !pair) return;
    // Alone: only the ringer's two bells. The whole choir: every bell, the ringer's
    // included (from the master once it has loaded, which holds their part too).
    const fromMaster = piece.parts.some(p => p.id.startsWith('full-'));
    for (const p of piece.parts) {
      if (p.id === pair.id) a.setLevel(p.id, plays === 'mine' || !fromMaster ? 0.75 : 0);
      else a.setLevel(p.id, plays === 'full' ? 0.75 : 0);
    }
  }, [piece, pair, plays]);
  useEffect(() => { applyLevels(); }, [applyLevels]);

  const configFor = useCallback((c: Choice): PlanConfig => ({
    tempo, loop: c.kind === 'section' && loop, gapBars: 1, countInBars: countBars, clickDuring: clicks,
  }), [tempo, loop, countBars, clicks]);

  useEffect(() => {
    const t = transportRef.current, plan = planRef.current;
    if (t && plan && configRef.current) {
      const cfg = { ...configFor(choice) }; configRef.current = cfg;
      if (t.running) { t.configure(plan, cfg); t.retime(tempo); } else t.configure(plan, cfg);
    }
  }, [tempo, loop, countBars, clicks, choice, configFor]);

  const period = (plan: Plan, cfg: PlanConfig) => plan.beats + (cfg.loop ? (cfg.gapBars || 0) * plan.beatsPerBar + (((plan.lead - plan.tail) % plan.beatsPerBar) + plan.beatsPerBar) % plan.beatsPerBar : 0);

  const analyse = useCallback((pass: number) => {
    const plan = planRef.current, cfg = configRef.current;
    if (!plan || !cfg || !pair) return;
    const presses = pressesRef.current.filter(p => p.pass === pass);
    if (!presses.length) return;
    const expected = plan.notes.filter(n => n.part === pair.id).map(n => ({ local: n.beat, hand: handOf(n.midi), midi: n.midi }))
      .sort((a, b) => a.local - b.local);
    const used = new Set<number>();
    const marks: Mark[] = expected.map(e => {
      let best = -1, bestD = Infinity;
      presses.forEach((p, i) => { if (used.has(i) || p.hand !== e.hand) return; const d = p.local - e.local; if (Math.abs(d) <= 0.5 && Math.abs(d) < Math.abs(bestD)) { best = i; bestD = d; } });
      if (best < 0) return { ...e, status: 'missed' as const, delta: 0 };
      used.add(best);
      const a = Math.abs(bestD);
      return { ...e, delta: bestD, status: a <= 0.12 ? 'on' as const : a <= 0.28 ? 'near' as const : 'off' as const };
    });
    const extras = presses.filter((_, i) => !used.has(i)).map(p => ({ local: p.local, hand: p.hand }));
    const r = sectionRange(choice);
    setResult({ from: r ? r[0] : 1, to: r ? r[1] : N, beats: plan.beats, marks, extras, spans: plan.spans, bpb: plan.beatsPerBar });
    pressesRef.current = pressesRef.current.filter(p => p.pass > pass);
  }, [pair, handOf, sectionRange, choice, N]);

  const stopRaf = () => { cancelAnimationFrame(rafRef.current); rafRef.current = 0; };
  const tick = useCallback(() => {
    const t = transportRef.current, plan = planRef.current, v = viewRef.current;
    if (!t || !plan) return;
    const st = t.state();
    const inMusic = st.phase === 'playing';
    const c = v?.showPlace(inMusic ? st.measure : null, inMusic ? st.sourceBeat : null);
    if (inMusic) {
      setMeasureNow(st.measure);
      setStatus(`Measure ${piece?.measures[st.measure - 1]?.label ?? st.measure}${st.pass > 1 ? `, time ${st.pass}` : ''}`);
    } else if (st.phase === 'count-in') setStatus(`Counting in: ${st.beat}`);
    else if (st.phase === 'gap') setStatus(`Again in ${st.barsLeft} bar${st.barsLeft === 1 ? '' : 's'}: ${st.beat}`);
    if (st.pass !== lastPassRef.current) { if (lastPassRef.current > 0) analyse(lastPassRef.current); lastPassRef.current = st.pass; }
    if (st.phase === 'gap' && pressesRef.current.some(p => p.pass === st.pass)) analyse(st.pass);
    // Keep the line being played in view.
    if (c && c.system !== systemRef.current && scoreBox.current && !scoreBox.current.classList.contains('whole')) {
      systemRef.current = c.system;
      const box = scoreBox.current, rect = c.rect.getBoundingClientRect(), br = box.getBoundingClientRect();
      if (box.scrollHeight > box.clientHeight + 4) box.scrollTo({ top: Math.max(0, box.scrollTop + rect.top - br.top - box.clientHeight * 0.18), behavior: 'smooth' });
      // On a phone the page itself scrolls: keep the line in the upper part of the screen, above the dock.
      else window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - window.innerHeight * 0.22), behavior: 'smooth' });
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [piece, analyse]);

  const start = useCallback((c: Choice) => {
    if (!piece) return;
    const r = sectionRange(c); if (!r) return;
    const a = ensureAudio(); applyLevels();
    if (!transportRef.current) transportRef.current = new Core.Transport(a, s => {
      if (s === 'ended') {
        setPlaying(false); setStatus('Finished'); stopRaf();
        if (lastPassRef.current > 0) analyse(lastPassRef.current);
        viewRef.current?.showPlace(null, null);
      }
    });
    const plan = Core.makePlan(piece, { range: { start: r[0], end: r[1] } });
    const cfg = configFor(c);
    planRef.current = plan; configRef.current = cfg; pressesRef.current = []; lastPassRef.current = 0; systemRef.current = -1;
    transportRef.current.configure(plan, cfg);
    transportRef.current.play(-Core.countIn(plan, cfg));
    setPlaying(true); stopRaf(); rafRef.current = requestAnimationFrame(tick);
  }, [piece, sectionRange, applyLevels, configFor, tick, analyse]);

  const pause = () => { transportRef.current?.pause(); setPlaying(false); setStatus('Paused'); stopRaf(); if (lastPassRef.current > 0) analyse(lastPassRef.current); };
  const resume = () => {
    const t = transportRef.current;
    if (t && planRef.current && !t.running && t.current() > 0 && status === 'Paused') { ensureAudio(); t.play(t.current()); setPlaying(true); rafRef.current = requestAnimationFrame(tick); }
    else start(choice);
  };
  const toggle = () => (playing ? pause() : resume());
  const choose = (c: Choice, andPlay = false) => {
    transportRef.current?.stop(0); stopRaf(); setPlaying(false); setStatus('Stopped');
    viewRef.current?.showPlace(null, null); setChoice(c); setResult(null);
    if (andPlay) setTimeout(() => start(c), 0);
  };
  useEffect(() => () => { stopRaf(); transportRef.current?.stop(0); }, []);

  // ---------- ringing along
  const ring = useCallback((handPressed: Hand) => {
    const hand: Hand = solo ? 'L' : handPressed;
    const a = ensureAudio();
    a.strike(hand === 'L' ? lowMidi : highMidi, 0.85);
    setPressed(hand); setTimeout(() => setPressed(p => (p === hand ? null : p)), 140);
    const t = transportRef.current, plan = planRef.current, cfg = configRef.current;
    if (!t || !t.running || !plan || !cfg) return;
    const q = t.beatAt(a.context.currentTime - a.latency());
    if (q == null) return;
    const pos = Core.position(plan, q, cfg);
    let pass = pos.pass, local = pos.local;
    if (pos.phase === 'count-in') { pass = 1; local = q; }
    else if (pos.phase === 'gap') { const per = period(plan, cfg); if (per - pos.local < 0.5) { pass = pos.pass + 1; local = pos.local - per; } }
    pressesRef.current.push({ pass, local, hand });
  }, [lowMidi, highMidi, solo]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); ring('L'); }
      else if (e.key === 'j' || e.key === 'J') { e.preventDefault(); ring('R'); }
      else if (e.key === ' ' && tag !== 'BUTTON') { e.preventDefault(); toggle(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---------- the score as a place to click and drag
  const onPointerDown = (e: React.PointerEvent) => {
    const m = viewRef.current?.measureFromPoint(e.clientX, e.clientY); if (!m) return;
    dragRef.current = { anchor: m, moved: false };
    if (e.pointerType === 'mouse') { e.preventDefault(); viewRef.current?.showDraft(m, m); }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current; if (!d || e.pointerType !== 'mouse') return;
    const m = viewRef.current?.measureFromPoint(e.clientX, e.clientY);
    if (m && m !== d.anchor) d.moved = true;
    if (m) viewRef.current?.showDraft(d.anchor, m);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d) return;
    const m = viewRef.current?.measureFromPoint(e.clientX, e.clientY) || d.anchor;
    viewRef.current?.showDraft(null, null);
    if (d.moved && m !== d.anchor) {
      setForm({ from: String(Math.min(d.anchor, m)), to: String(Math.max(d.anchor, m)), name: '' }); setFormErr('');
      setTimeout(() => nameRef.current?.focus(), 0);
    } else choose({ kind: 'from', measure: d.anchor }, true);
  };

  // ---------- sections
  const saveSection = () => {
    const from = Number(form.from), to = Number(form.to);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to > N || from > to) { setFormErr(`Use measures from 1 to ${N}, the first no later than the last.`); return; }
    const s: Section = { id: Math.random().toString(36).slice(2, 9), name: form.name.trim() || `Measures ${from} to ${to}`, from, to };
    setSections(list => [...list, s]); setForm({ from: '', to: '', name: '' }); setFormErr('');
    choose({ kind: 'section', id: s.id });
  };
  const removeSection = (id: string) => {
    setSections(list => list.filter(s => s.id !== id)); setConfirmRemove(null);
    if (choice.kind === 'section' && choice.id === id) choose({ kind: 'whole' });
  };
  const current = choice.kind === 'section' ? sections.find(s => s.id === choice.id) : null;
  const choiceLabel = current ? current.name : choice.kind === 'from' ? `From measure ${choice.measure}` : 'The whole piece';
  const choiceSub = current ? (current.name.startsWith('Measures ') ? 'Your section' : `Measures ${current.from} to ${current.to}`) : choice.kind === 'from' ? `Measures ${choice.measure} to ${N}` : `Measures 1 to ${N}`;

  // ---------- pieces of the page
  const bells = pair ? pair.label.split(' · ') : ['', ''];
  const pairPicker = (
    <div className="bl-scrim" role="dialog" aria-modal="true" aria-labelledby="bl-pick-title" onClick={() => pair && setPicking(false)}>
      <div className="bl-picker" onClick={e => e.stopPropagation()}>
        <h2 id="bl-pick-title">Which bells do you ring?</h2>
        <p>Choose your pair. The page shows your part with the melody above it, and the rest of the choir plays with you. You can change this at any time.</p>
        <div className="bl-pairs">
          {index?.pairs.map(p => (
            <button key={p.id} className={`bl-pair${p.id === pairId ? ' mine' : ''}`} disabled={!p.ready}
              onClick={() => { setPairId(p.id); store.set('pair', p.id); setPicking(false); choose({ kind: 'whole' }); }}>
              <b>{p.label}</b><span>{p.id === pairId ? 'Your bells' : p.ready ? 'Ready' : 'Not engraved yet'}</span>
            </button>))}
        </div>
        {pair && <div className="bl-picker-foot"><button className="bl-btn q" onClick={() => setPicking(false)}>Close</button></div>}
      </div>
    </div>
  );

  const sectionsCard = (cls: string) => (
    <section className={`bl-card navy ${cls}`}><div className="bl-in bl-secs">
      <p className="bl-eb">Your sections</p>
      <h2>Practise a part</h2>
      {sections.length === 0 && <p className="bl-muted bl-small">You have no sections yet. Make one below, or drag across the music from its first measure to its last.</p>}
      {sections.map(s => {
        const on = choice.kind === 'section' && choice.id === s.id;
        return (
          <div key={s.id} className={`bl-s${on ? ' on' : ''}`}>
            <button className="bl-s-name" onClick={() => choose({ kind: 'section', id: s.id })}>
              <span className="nm">{s.name}</span><span className="mm">{on ? (playing ? 'Looping now' : 'Chosen') : `Measures ${s.from} to ${s.to}`}</span>
            </button>
            <span className="gap">
              {confirmRemove === s.id
                ? <><button className="bl-sm danger" onClick={() => removeSection(s.id)}>Remove it</button><button className="bl-sm" onClick={() => setConfirmRemove(null)}>Keep</button></>
                : on ? <button className="bl-sm" onClick={() => setConfirmRemove(s.id)}>Remove</button>
                  : <button className="bl-sm" onClick={() => choose({ kind: 'section', id: s.id }, true)}>Loop</button>}
            </span>
          </div>);
      })}
      <div className="bl-whole">
        <button className={`bl-sm${choice.kind === 'whole' ? ' on' : ''}`} onClick={() => choose({ kind: 'whole' }, true)}>Play the whole piece</button>
        <span>Measures 1 to {N || 110}</span>
      </div>
      <div className="bl-new">
        <span className="bl-lab strong">New section</span>
        <div className="fields">
          <label>From<input inputMode="numeric" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} /></label>
          <label>To<input inputMode="numeric" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} /></label>
          <label className="nm">Name (if you like)<input ref={nameRef} className="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') saveSection(); }} /></label>
          <button className="bl-btn navy" onClick={saveSection}>Save</button>
        </div>
        {formErr && <p className="bl-err">{formErr}</p>}
        <p>Or drag across the music from the first measure to the last. Your sections are kept in this browser.</p>
      </div>
    </div></section>
  );

  const markedLo = index?.marked[0] || 144, markedHi = index?.marked[1] || 180, tMin = 60, tMax = 192;
  const pct = (v: number) => `${((v - tMin) / (tMax - tMin)) * 100}%`;
  const playControls = (big: boolean) => (
    <>
      {big && (
        <div className="bl-big">
          <button className="bl-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>{playing ? '❚❚' : '▶'}</button>
          <div className="bl-tempo">
            <div className="v">{tempo} <small>beats a minute</small></div>
            <div className="bl-status">{choiceLabel} · {status}</div>
          </div>
        </div>)}
      <div className="bl-slider">
        {!big && <div className="v">{tempo} <small>beats a minute</small></div>}
        <div className="bl-range">
          <span className="marked" style={{ left: pct(markedLo), width: `calc(${pct(markedHi)} - ${pct(markedLo)})` }} />
          <input type="range" min={tMin} max={tMax} step={4} value={tempo} aria-label="Speed in beats a minute" onChange={e => setTempo(Number(e.target.value))} />
        </div>
        <div className="mk">The gold line is the speed marked in the score, {markedLo} to {markedHi}.</div>
      </div>
      {choice.kind === 'section' && current && (
        <label className="bl-loopline"><input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} />
          <span><b>Loop measures {current.from} to {current.to}</b><br /><span className="bl-muted bl-small">One bar of counting between times through</span></span></label>)}
      <div className="bl-opt"><span className="bl-lab">Before it starts</span>
        <div className="bl-seg">{[0, 1, 2].map(n => <button key={n} className={countBars === n ? 'on' : ''} onClick={() => setCountBars(n)}>{n === 0 ? 'No count' : `Count ${n} bar${n > 1 ? 's' : ''}`}</button>)}</div></div>
      <div className="bl-opt"><span className="bl-lab">What plays</span>
        <div className="bl-seg"><button className={plays === 'mine' ? 'on' : ''} onClick={() => setPlays('mine')}>My bells alone</button><button className={plays === 'full' ? 'on' : ''} onClick={() => setPlays('full')}>The whole choir</button></div></div>
      <label className="bl-check"><input type="checkbox" checked={clicks} onChange={e => setClicks(e.target.checked)} /> Click the beat while it plays</label>
    </>
  );

  const keyBox = (hand: Hand, thumbs: boolean) => {
    if (solo && hand === 'R') return null;
    const name = bells[hand === 'L' ? 0 : 1];
    const handText = solo ? (thumbs ? 'Either thumb' : 'Either hand') : thumbs ? (hand === 'L' ? 'Left thumb' : 'Right thumb') : hand === 'L' ? 'Left hand' : 'Right hand';
    return (
      <button className={`bl-key${pressed === hand ? ' pressed' : ''}`} onPointerDown={e => { e.preventDefault(); ring(hand); }} aria-label={`Ring ${name}`}>
        {!thumbs && <kbd>{solo ? 'F or J' : hand === 'L' ? 'F' : 'J'}</kbd>}
        <span className="bell">{name}</span>
        <span className="hand">{handText}</span>
      </button>);
  };

  const resultView = result && (() => {
    const total = result.marks.length, on = result.marks.filter(m => m.status === 'on').length;
    const where = (local: number) => {
      let i = result.spans.length - 1; while (i > 0 && result.spans[i].beat > local + 1e-6) i--;
      const s = result.spans[i]; return { measure: piece?.measures[s.measure - 1]?.label ?? String(s.measure), beat: Math.floor(local - s.beat + 1e-6) + 1 };
    };
    const issues = result.marks.filter(m => m.status !== 'on').slice(0, 3).map(m => {
      const w = where(m.local), b = bellName(m.midi);
      if (m.status === 'missed') return `${b} was missed on beat ${w.beat} of measure ${w.measure}`;
      return `${b} came ${m.status === 'near' ? 'a little ' : ''}${m.delta < 0 ? 'early' : 'late'} in measure ${w.measure}`;
    });
    const W = result.beats, lanes: Hand[] = solo ? ['L'] : ['L', 'R'];
    const colour = (s: Mark['status']) => (s === 'on' ? '#2D5A3D' : s === 'near' ? '#C9A227' : '#722F37');
    const minW = Math.max(100, W * 9);
    const x = (beat: number) => `${(Math.max(0, Math.min(W, beat)) / W) * 100}%`;
    return (
      <div className="bl-result">
        <span className="bl-lab">Last time through, measures {result.from} to {result.to}</span>
        <div className="bl-tl-scroll"><div className="bl-tl" style={{ minWidth: minW }}>
          {Array.from({ length: Math.ceil(W) + 1 }, (_, b) => <span key={b} className={b % result.bpb === 0 ? 'bar' : 'beat'} style={{ left: x(b) }} />)}
          <div className="ruler">{result.spans.map(sp => <b key={sp.beat} style={{ left: x(sp.beat) }}>{piece?.measures[sp.measure - 1]?.label ?? sp.measure}</b>)}</div>
          {lanes.map(h => <div key={h} className="lane">
            <em>{solo ? `${bells[0]} · F or J` : h === 'L' ? `${bells[0]} · F` : `${bells[1]} · J`}</em>
            {result.marks.filter(m => m.hand === h).map((m, i) => (
              <React.Fragment key={i}>
                <i className={m.status === 'missed' ? 'w missed' : 'w'} style={{ left: x(m.local) }} />
                {m.status !== 'missed' && <i className="p" style={{ left: x(m.local + m.delta), background: colour(m.status) }} />}
              </React.Fragment>))}
            {result.extras.filter(e => e.hand === h).map((e, i) => <i key={`x${i}`} className="p extra" style={{ left: x(e.local) }} />)}
          </div>)}
        </div></div>
        <div className="bl-legend"><span><i className="w" />Written</span><span><i style={{ background: '#2D5A3D' }} />On the beat</span><span><i style={{ background: '#C9A227' }} />A little off</span><span><i style={{ background: '#722F37' }} />Off, or missed</span></div>
        <p className="bl-sum"><b>{on} of {total}</b> on the beat.{issues.length ? ` ${issues.join('; ')}${result.marks.filter(m => m.status !== 'on').length > 3 ? ', and more' : ''}.` : ''}</p>
      </div>);
  })();

  if (error) return <div className="bells"><div className="bl-wrap"><p className="bl-err">{error}</p></div></div>;

  return (
    <div className="bells">
      <div className="bl-site"><Link to="/">Liturgical Music</Link><span>Signed in as {signedInAs}</span></div>
      <header className="bl-band">
        <div className="titles">
          <div className="eyebrow">St. Luke's ~ San Lucas · Bells</div>
          <h1>{index?.title || 'Bring a Torch, Jeanette, Isabella'}</h1>
          <p>{index ? `${index.arranger}. ${index.occasion}` : ''}</p>
        </div>
        <button className="bl-mine" onClick={() => setPicking(true)}><small>Your bells</small> <b>{pair ? pair.label : 'Choose'}</b> <u>Change</u></button>
      </header>

      <div className="bl-wrap"><div className="bl-grid">
        <aside className="bl-left">{sectionsCard('')}</aside>

        <main className="bl-main">
          <div className="bl-phsec">
            <span><span className="nm">{choiceLabel}</span><br /><span className="mm">{choiceSub} · {status}</span></span>
            <button className="bl-sm" onClick={() => setSheet('sections')}>Sections</button>
          </div>
          <section className="bl-card bl-score">
            <div className="bar"><h2>Your part</h2><span className="bl-muted sub">The melody and words above, {solo ? 'your bell' : 'your two bells'} below</span>
              <span className="where">{measureNow ? <>Measure <b>{piece?.measures[measureNow - 1]?.label}</b> of {N}</> : `${N} measures`}</span>
              <span className="bl-size" role="group" aria-label="Size of the music">
                <button className={size.mode === 'whole' ? 'on' : ''} onClick={() => setSize({ mode: 'whole' })}>Whole piece</button>
                <button onClick={() => zoomBy(-0.1)} aria-label="Smaller">−</button>
                <button className={`pct${size.mode === 'zoom' ? ' on' : ''}`} onClick={() => setSize({ mode: 'zoom', z: 1 })} title="Back to the panel's width">{size.mode === 'zoom' ? `${Math.round(size.z * 100)}%` : 'Width'}</button>
                <button onClick={() => zoomBy(0.1)} aria-label="Larger">+</button>
              </span></div>
            <div className={`bl-page${size.mode === 'whole' ? ' whole' : ''}`} ref={scoreBox} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
              <div className={`bl-svg${size.mode === 'whole' ? ' bl-src-hidden' : ''}`} ref={svgHost} />
              {size.mode === 'whole' && whole && (
                <div className="bl-cols">
                  {whole.cols.map((c, i) => (
                    <svg key={i} className="bl-col" viewBox={`${whole.x} ${c.y0} ${whole.w} ${c.y1 - c.y0}`}
                      width={whole.w * whole.scale} height={(c.y1 - c.y0) * whole.scale}><use href="#bl-page" /></svg>))}
                </div>)}
              {!svgText && <p className="bl-muted" style={{ padding: 20 }}>{pair ? 'Loading the music…' : 'Choose your bells to see your part.'}</p>}
            </div>
            <div className="bl-marks">R ring · TD thumb damp · SK shake · + over a dot: mallet on the table · LV let vibrate. Click a measure to start there; drag across measures to make a section.</div>
          </section>
          {sectionsCard('bl-under')}
        </main>

        <aside className="bl-right">
          <section className="bl-card gold bl-play-card"><div className="bl-in"><p className="bl-eb">Play along</p>{playControls(true)}</div></section>
          <section className="bl-card bl-ring"><div className="bl-in">
            <p className="bl-eb">Ring along on the keyboard</p>
            <div className="bl-keys">{keyBox('L', false)}{keyBox('R', false)}</div>
            <p className="bl-muted bl-small">{solo ? 'Press F or J when your bell rings. On a phone, tap the bell with a thumb.' : 'Press F when your left bell rings and J when your right one does. On a phone, tap the two bells with your thumbs.'}</p>
            {resultView || <p className="bl-muted bl-small">Ring along while it plays, and each time through you will see how your strokes lined up with the beat.</p>}
          </div></section>
        </aside>
      </div></div>

      <div className="bl-dock">
        <div className="r1"><button className="bl-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>{playing ? '❚❚' : '▶'}</button>
          <span className="t">{tempo} <small>a minute · {countBars ? `count ${countBars}` : 'no count'} · {plays === 'mine' ? 'my bells alone' : 'whole choir'}</small></span>
          <button className="bl-sm" onClick={() => setSheet('more')}>More</button></div>
        <div className="bl-keys">{keyBox('L', true)}{keyBox('R', true)}</div>
      </div>

      {sheet && (
        <div className="bl-scrim bl-sheet-scrim" onClick={() => setSheet(null)}>
          <div className="bl-sheet" onClick={e => e.stopPropagation()}>
            <div className="bl-sheet-head"><button className="bl-sm" onClick={() => setSheet(null)}>Done</button></div>
            {sheet === 'sections' ? sectionsCard('bl-in-sheet') : (
              <><section className="bl-card gold"><div className="bl-in"><p className="bl-eb">Play along</p>{playControls(false)}</div></section>
                {resultView && <section className="bl-card"><div className="bl-in">{resultView}</div></section>}</>)}
          </div>
        </div>)}
      {picking && index && pairPicker}
    </div>
  );
}

export default BellsPage;
